/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getAdmins, {
	GetAdminsParams,
	GetAdminsResponse,
} from 'api/admin/getAdmins'
import { AdminUser } from 'types'

/**
 * Types for the slice of admin in the store
 */
export interface AdminState {
	records: AdminUser[]
	totalCount: number
	totalPages: number
	currentPage: number
	loading: boolean
}

/**
 * Initial state of the slice admin
 */
const initialState: AdminState = {
	records: [],
	totalCount: 0,
	totalPages: 0,
	currentPage: 1,
	loading: false,
}

/**
 * Action for getting admins
 *
 * Tries to get admins from the API and returns the result in the payload
 *
 * @param {GetAdminsParams} options - Options for the API call
 * @returns {Promise<GetAdminsResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getAdminsAction = createAsyncThunk<
	GetAdminsResponse,
	GetAdminsParams
>('admin/getAdminsAction', async (options, { rejectWithValue }) => {
	try {
		const response = await getAdmins(options)
		return response
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const adminSlice = createSlice({
	name: 'admin',
	initialState,
	reducers: {
		clearAdmin: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getAdminsAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getAdminsAction.fulfilled, (state, action) => {
			state.records = action.payload.records
			state.totalCount = action.payload.totalCount
			state.totalPages = action.payload.totalPages
			state.currentPage = action.payload.currentPage
			state.loading = false
		})
		builder.addCase(getAdminsAction.rejected, state => {
			state.loading = false
		})
	},
})

export const { clearAdmin } = adminSlice.actions

export default adminSlice.reducer
